// src/components/chat/ConversationsSidebar.js
/**
 * Layout with item thumbnails, unread badges, time stamps
 * Supports product chats, admin/support chats
 */
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, MessageCircle, X, ShoppingBag, Shield, Trash2 } from 'lucide-react';
import { listenToInbox } from '../../utils/chatUtils';
import useAuth from '../../hooks/useAuth';
import { userRepository } from '../../repositories/userRepository';
import { isChatUnreadForUser } from '../../utils/unread';
import { deleteConversationForUser } from '../../services/chatService';
import ConfirmDialog from '../ui/ConfirmDialog';
import { errorDev } from '../../utils/logger.js';

const userProfileCache = new Map();

function formatTime(timestamp) {
  if (!timestamp) return '';
  try {
    let date;
    if (timestamp?.seconds) date = new Date(timestamp.seconds * 1000);
    else if (typeof timestamp === 'number') date = new Date(timestamp);
    else date = new Date(timestamp);

    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    if (days < 30) return `${Math.floor(days / 7)}w`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (err) {
    errorDev("Operation failed:", err);
    return '';
  }
}

function truncate(s, len = 55) {
  if (!s) return '';
  const str = String(s);
  return str.length > len ? str.slice(0, len - 1) + '\u2026' : str;
}

function getPreview(chat) {
  if (chat.lastMessage) {
    if (typeof chat.lastMessage === 'string') {
      const normalized = chat.lastMessage.match(/^Offer:\s*(\d+(?:[.,]\d+)*)$/i);
      if (normalized?.[1]) {
        const amount = Number(String(normalized[1]).replace(/,/g, ''));
        return Number.isFinite(amount) ? `Offer proposed: ${amount.toLocaleString()} RWF` : 'Offer proposed';
      }
      return chat.lastMessage;
    }
    if (chat.lastMessage.text) return String(chat.lastMessage.text);
  }
  if (chat.lastOfferAmount) return `Offer proposed: ${Number(chat.lastOfferAmount).toLocaleString()} RWF`;
  if (chat.lastMessageType === 'offer') return 'Offer proposed';
  if (chat.lastMessageType === 'location') return 'Shared location';
  if (chat.lastMessageType === 'image') return 'Photo';
  return '';
}

function getContextBadge(chat) {
  if (chat.contextType === 'product' || chat.productId) {
    return { icon: ShoppingBag, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/30', label: 'Product' };
  }
  if (chat.contextType === 'admin' || chat.contextType === 'support') {
    return { icon: Shield, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'Support' };
  }
  return null;
}

export default function ConversationsSidebar({
  onSelectConversation,
  selectedChatId,
  fullWidth = false,
}) {
  const { t } = useTranslation(['inbox', 'common']);
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    chatId: null,
    loading: false,
  });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!user) {
      setConversations([]);
      setLoading(false);
      return;
    }

    const unsubscribe = listenToInbox(user.uid, async (chats) => {
      if (!mountedRef.current) return;

      const chatList = chats || [];

      // Batch-fetch uncached participant profiles to avoid N+1 getById calls
      const uncachedIds = Array.from(new Set(
        chatList
          .map((chat) => (chat.participants || []).find((id) => id !== user.uid))
          .filter((id) => id && !userProfileCache.has(id))
      ));

      if (uncachedIds.length > 0) {
        const profiles = await userRepository.getByIds(uncachedIds);
        const profilesById = new Map(profiles.map((p) => [p.id, p]));
        uncachedIds.forEach((id) => {
          userProfileCache.set(id, profilesById.has(id) ? { ...profilesById.get(id) } : { id });
        });
      }

      const enriched = await Promise.all(
        chatList.map(async (chat) => {
          const otherId = (chat.participants || []).find((id) => id !== user.uid);
          if (!otherId) return { ...chat, other: {} };

          const other = userProfileCache.get(otherId) || { id: otherId };
          return { ...chat, other };
        })
      );

      if (mountedRef.current) {
        setConversations(enriched);
        setLoading(false);
      }
    });

    return () => { unsubscribe && unsubscribe(); };
  }, [user]);

  const filtered = conversations.filter((c) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    const name = c.other?.displayName || c.other?.name || '';
    const preview = getPreview(c);
    const title = c.productTitle || '';
    const supportLabel = c.contextType === 'support' ? 'support center' : '';
    return name.toLowerCase().includes(q) || preview.toLowerCase().includes(q) || title.toLowerCase().includes(q) || supportLabel.includes(q);
  });

  const handleRequestDelete = async (chatId) => {
    if (!user?.uid || !chatId) return;
    setConfirmDialog({ open: true, chatId, loading: false });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-card">
        <div className="flex flex-col items-center gap-3">
          <div className="orrith-loader orrith-loader-md orrith-loader-primary" />
          <p className="text-sm text-muted-foreground">
            {t('inbox:loadingConversations', 'Loading conversations\u2026')}
          </p>
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 bg-card">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <MessageCircle className="w-8 h-8 text-muted-foreground" />
        </div>
<h3 className="text-base font-semibold text-foreground mb-1">
            {t('inbox:emptyInbox', 'Your inbox is empty')}
          </h3>
          <p className="text-sm text-muted-foreground text-center max-w-xs">
            {t('inbox:emptyInboxDescription', 'When you message a seller, your conversations will appear here.')}
          </p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full bg-card ${fullWidth ? '' : 'border-r border-border'}`}>
      {/* Search bar */}
      <div className="px-3 py-2.5 border-b border-border flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('inbox:searchPlaceholder', 'Search conversations\u2026')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-9 py-2 bg-gray-100 dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-green-500/30 text-sm"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <p className="text-sm text-muted-foreground">
              {t('inbox:noSearchResults', 'No conversations found')}
            </p>
            <button
              onClick={() => setSearchTerm('')}
              className="mt-2 text-sm text-green-600 dark:text-green-400 hover:underline"
            >
              {t('inbox:clearSearch', 'Clear search')}
            </button>
          </div>
        ) : (
          <ul className="space-y-2 px-2 py-2">
            {filtered.map((chat) => {
              const isSelected = String(chat.id) === String(selectedChatId);
              const other = chat.other || {};
              const supportViewer = chat.contextType === 'support' && String(chat.supportUserId || '') === String(user.uid);
              const name = supportViewer
                ? t('inbox:supportCenterTitle', 'Support Center')
                : (other.displayName || other.name || other.email?.split('@')[0] || 'User');
              const avatar = other.photo || other.photoURL;
              const initials = supportViewer ? 'SC' : name.slice(0, 2).toUpperCase();
              const preview = truncate(getPreview(chat) || t('inbox:startChatting', 'Start chatting\u2026'));
              const timeStr = formatTime(chat.updatedAt || chat.lastMessageAt);
              const badge = getContextBadge(chat);
              const itemTitle = chat.productTitle || '';

              const hasUnread = isChatUnreadForUser(chat, user.uid);

              return (
                <li key={chat.id}>
                  <div
                    className={`w-full text-left px-3 py-3 rounded-xl border border-transparent flex items-start gap-3 transition-all duration-150 ${
                      isSelected
                        ? 'bg-green-50 dark:bg-green-900/15 border-green-200 dark:border-green-900/50'
                        : hasUnread
                          ? 'bg-green-50/40 dark:bg-green-950/10 hover:bg-muted/60'
                          : 'hover:bg-muted/50'
                    }`}
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center overflow-hidden border border-border">
                        {avatar ? (
                          <img
                            src={avatar}
                            alt={name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <span className="text-sm font-bold text-foreground">{initials}</span>
                        )}
                      </div>
                      {hasUnread && (
                        <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-card" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => onSelectConversation(chat.id, other.id)}
                          className="min-w-0 text-left"
                        >
                          <h4 className={`text-sm truncate ${hasUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground/80'}`}>
                            {name}
                          </h4>
                        </button>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground flex-shrink-0">{timeStr}</span>
                          <button
                            type="button"
                            onClick={() => handleRequestDelete(chat.id)}
                            className="inline-flex items-center justify-center w-6 h-6 rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                            aria-label={t('inbox:deleteConversation', 'Delete conversation')}
                            title={t('inbox:deleteConversation', 'Delete conversation')}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => onSelectConversation(chat.id, other.id)}
                        className={`mt-0.5 w-full text-left flex items-center gap-2 ${hasUnread ? 'text-foreground/80' : 'text-muted-foreground'}`}
                      >
                        {badge && (
                          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${badge.bg} ${badge.color} shrink-0`}>
                            <badge.icon className="w-2.5 h-2.5" />
                            {badge.label}
                          </span>
                        )}
                        <p className={`text-sm truncate ${hasUnread ? 'font-medium' : ''}`}>{preview}</p>
                        {itemTitle && (
                          <span className="text-xs text-muted-foreground/70 truncate">{truncate(itemTitle, 26)}</span>
                        )}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        title={t('inbox:deleteConversation', 'Delete conversation')}
        message={t('inbox:deleteConversationConfirm', 'Delete this conversation from your inbox?')}
        confirmLabel={t('inbox:delete', 'Delete')}
        destructive
        loading={confirmDialog.loading}
        onCancel={() => {
          if (confirmDialog.loading) return;
          setConfirmDialog({ open: false, chatId: null, loading: false });
        }}
        onConfirm={async () => {
          if (!user?.uid || !confirmDialog.chatId) return;
          setConfirmDialog((prev) => ({ ...prev, loading: true }));
          try {
            await deleteConversationForUser({ chatId: confirmDialog.chatId, userId: user.uid });
            setConfirmDialog({ open: false, chatId: null, loading: false });
          } catch (err) {
            errorDev("Operation failed:", err);
            setConfirmDialog((prev) => ({ ...prev, loading: false }));
          }
        }}
      />
    </div>
  );
}
